import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface NavbarProps {
  onSignClick?: () => void;
}

export default function Navbar({ onSignClick }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 서브페이지에서는 항상 흰 배경 + 어두운 텍스트
  const isSolid = !isHomePage || isScrolled;

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const handleSignClick = () => {
    navigate('/sign');
    setIsMobileMenuOpen(false);
  };

  const handleResourceClick = () => {
    navigate('/resource');
    setIsMobileMenuOpen(false);
  };

  const handleHomeClick = () => {
    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isSolid ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* 로고 - 클릭 시 홈으로 이동 */}
          <div
            className="flex items-center space-x-2 cursor-pointer"
            onClick={handleHomeClick}
          >
            <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center">
              <i className="ri-scales-3-line text-2xl md:text-3xl text-red-600"></i>
            </div>
            <span className={`text-base md:text-xl font-bold ${isSolid ? 'text-gray-900' : 'text-white'}`}>
              사법정의를 위한 국민연대
            </span>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            <button
              onClick={handleHomeClick}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isSolid ? 'text-gray-700 hover:text-red-600' : 'text-white hover:text-red-200'
              }`}
            >
              홈
            </button>
            <button
              onClick={() => scrollToSection('declaration')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isSolid ? 'text-gray-700 hover:text-red-600' : 'text-white hover:text-red-200'
              }`}
            >
              선언서
            </button>
            <button
              onClick={() => scrollToSection('statistics')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isSolid ? 'text-gray-700 hover:text-red-600' : 'text-white hover:text-red-200'
              }`}
            >
              참여현황
            </button>
            <button
              onClick={() => scrollToSection('comments')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isSolid ? 'text-gray-700 hover:text-red-600' : 'text-white hover:text-red-200'
              }`}
            >
              응원메시지
            </button>
            <button
              onClick={() => scrollToSection('infographic')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isSolid ? 'text-gray-700 hover:text-red-600' : 'text-white hover:text-red-200'
              }`}
            >
              인포그래픽
            </button>
            <button
              onClick={() => scrollToSection('timeline')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isSolid ? 'text-gray-700 hover:text-red-600' : 'text-white hover:text-red-200'
              }`}
            >
              타임라인
            </button>
            <button
              onClick={handleResourceClick}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isSolid ? 'text-gray-700 hover:text-red-600' : 'text-white hover:text-red-200'
              }`}
            >
              자료실
            </button>
            <button
              onClick={() => scrollToSection('donation')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isSolid ? 'text-gray-700 hover:text-red-600' : 'text-white hover:text-red-200'
              }`}
            >
              함께하기
            </button>
          </div>

          {/* Desktop Sign Button */}
          <button
            onClick={handleSignClick}
            className="hidden md:block bg-red-600 text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-red-700 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
          >
            서명하기
          </button>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              isSolid ? 'text-gray-900' : 'text-white'
            }`}
          >
            <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-300 ease-in-out overflow-hidden ${
          isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        } ${isSolid ? 'bg-white' : 'bg-black/95 backdrop-blur-sm'}`}
      >
        <div className="px-4 py-4 space-y-3">
          {[
            { label: '홈', action: () => { handleHomeClick(); } },
            { label: '선언서', id: 'declaration' },
            { label: '참여현황', id: 'statistics' },
            { label: '응원메시지', id: 'comments' },
            { label: '인포그래픽', id: 'infographic' },
            { label: '타임라인', id: 'timeline' },
          ].map(({ label, id, action }) => (
            <button
              key={label}
              onClick={action ?? (() => scrollToSection(id!))}
              className={`block w-full text-left py-3 px-4 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                isSolid
                  ? 'text-gray-700 hover:bg-gray-100 hover:text-red-600'
                  : 'text-white hover:bg-white/10 hover:text-red-200'
              }`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={handleResourceClick}
            className={`block w-full text-left py-3 px-4 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
              isSolid
                ? 'text-gray-700 hover:bg-gray-100 hover:text-red-600'
                : 'text-white hover:bg-white/10 hover:text-red-200'
            }`}
          >
            자료실
          </button>
          <button
            onClick={() => scrollToSection('donation')}
            className={`block w-full text-left py-3 px-4 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
              isSolid
                ? 'text-gray-700 hover:bg-gray-100 hover:text-red-600'
                : 'text-white hover:bg-white/10 hover:text-red-200'
            }`}
          >
            함께하기
          </button>
          <button
            onClick={handleSignClick}
            className="block w-full bg-red-600 text-white py-3 px-4 rounded-full text-sm font-semibold hover:bg-red-700 transition-colors shadow-lg cursor-pointer"
          >
            서명하기
          </button>
        </div>
      </div>
    </nav>
  );
}